// Resistances, immunities and vulnerabilities on an initiative row.
//
// The fourth strip in the row's fx-stack, beside conditions, light and vision,
// and built the same way: chips for what is in force, each removable, and one
// control to add. The initiative page is where combat is run, so what a creature
// shrugs off belongs where the DM is already looking rather than behind a panel.
//
// The strip shows what the sheet says AND what the DM has ruled, together, because
// at the table there is no difference — a behir resistant to lightning by its
// statblock and a goblin the DM has decided is resistant to fire because it is
// standing in a brazier are both just "resistant" when the damage lands. Where it
// came from is in the tooltip, and a sheet entry can be struck out without
// touching the sheet.
import { useState } from "react";
import {
  DEFENSE_LEVELS, DEFENSE_TYPE_OPTIONS, describeEntry, effectiveDefenses, entriesFromOption, entryKey,
  WEAPON_TYPES, type DefenseEntry, type DefenseLevel, type DefenseSource, type RowDefenses,
} from "../state/defenses";

const norm = (v: unknown) => String(v ?? "").trim().toLowerCase();
const EMPTY: RowDefenses = { added: [], removed: [] };

/** How long a granted defence lasts, in rounds. A minute is 10, an hour 600. */
const DURATIONS: { label: string; rounds: number | null }[] = [
  { label: "until removed", rounds: null },
  { label: "1 minute", rounds: 10 },
  { label: "10 minutes", rounds: 100 },
  { label: "1 hour", rounds: 600 },
  { label: "8 hours", rounds: 4800 },
];

/** Chips, with bludgeoning/piercing/slashing shown as one when all three match. */
interface Chip { key: string; text: string; title: string; entries: DefenseEntry[]; level: DefenseLevel }

function chipsFor(entries: DefenseEntry[]): Chip[] {
  const order: DefenseLevel[] = ["immune", "resist", "vulnerable"];
  const short = (l: DefenseLevel) => DEFENSE_LEVELS.find((x) => x.value === l)!.short;
  const weapon = new Map<string, DefenseEntry[]>();
  const rest: DefenseEntry[] = [];
  for (const d of entries) {
    if ((WEAPON_TYPES as readonly string[]).includes(norm(d.type))) {
      const k = `${d.level}:${d.nonmagicalOnly ? "n" : "m"}`;
      weapon.set(k, [...(weapon.get(k) || []), d]);
    } else rest.push(d);
  }
  const out: Chip[] = [];
  for (const [k, group] of weapon) {
    if (new Set(group.map((d) => norm(d.type))).size === WEAPON_TYPES.length) {
      out.push({
        key: `bps:${k}`, level: group[0].level, entries: group,
        text: `${short(group[0].level)} b/p/s${group[0].nonmagicalOnly ? "*" : ""}`,
        title: group.map(describeEntry).join("\n"),
      });
    } else rest.push(...group);
  }
  for (const d of rest) out.push({ key: d.id, level: d.level, entries: [d], text: `${short(d.level)} ${d.type}${d.nonmagicalOnly ? "*" : ""}`, title: describeEntry(d) });
  return out.sort((a, b) => order.indexOf(a.level) - order.indexOf(b.level) || a.text.localeCompare(b.text));
}

export function DefenseStrip({
  name, sheet, row, onChange, onLog,
}: {
  name: string;
  sheet: { resist?: string; immune?: string; vulnerable?: string } | null | undefined;
  row: RowDefenses | undefined;
  onChange: (next: RowDefenses | undefined) => void;
  onLog?: (text: string) => void;
}) {
  const [pending, setPending] = useState<{ level: DefenseLevel; option: string; source: DefenseSource; label: string; rounds: string } | null>(null);
  const cur: RowDefenses = row || EMPTY;
  const live = effectiveDefenses(sheet, row);
  const chips = chipsFor(live);

  const clean = (next: RowDefenses) => (next.added.length || next.removed.length ? next : undefined);

  const remove = (chip: Chip) => {
    const added = cur.added.filter((d) => !chip.entries.some((x) => x.id === d.id));
    // A sheet entry cannot be deleted, only struck out — the sheet is not ours.
    const struck = chip.entries.filter((d) => d.source === "sheet").map((d) => entryKey(d.level, d.type));
    onChange(clean({ added, removed: [...new Set([...cur.removed, ...struck])] }));
    onLog?.(`${name}: ${chip.text} removed`);
  };

  const add = () => {
    if (!pending) return;
    const option = DEFENSE_TYPE_OPTIONS.find((o) => o.key === pending.option);
    if (!option) { setPending(null); return; }
    const rounds = pending.rounds === "" ? null : Number(pending.rounds);
    const entries = entriesFromOption(option, pending.level, pending.source, {
      label: pending.label.trim() || undefined,
      rounds: Number.isFinite(rounds as number) && rounds ? rounds : null,
    });
    /* Un-strike anything this re-adds, so a DM who struck the sheet's fire
       resistance and then grants fire resistance from a potion gets the potion
       rather than nothing. */
    const readded = new Set(entries.map((d) => entryKey(d.level, d.type)));
    onChange(clean({ added: [...cur.added, ...entries], removed: cur.removed.filter((k) => !readded.has(norm(k))) }));
    const lvl = DEFENSE_LEVELS.find((l) => l.value === pending.level)!.label;
    onLog?.(`${name} is ${lvl} to ${option.label}${pending.label.trim() ? ` (${pending.label.trim()})` : ""}`);
    setPending(null);
  };

  return (
    <>
      {chips.map((c) => (
        <span key={c.key} className={`chip def-${c.level}`} title={c.title}>
          {c.text}
          <button aria-label={`Remove ${c.text} from ${name}`} title={`Remove ${c.text}`} onClick={() => remove(c)}>×</button>
        </span>
      ))}
      {!pending && (
        <button
          className="ghost small"
          title={`Add a resistance, immunity or vulnerability to ${name}. Weapon types are offered both plain and "from nonmagical attacks" — they are different defences.`}
          aria-label={`Add a defence to ${name}`}
          onClick={() => setPending({ level: "resist", option: "fire", source: "ruling", label: "", rounds: "" })}
        >
          + resist
        </button>
      )}
      {pending && (
        <span className="def-form" role="group" aria-label={`New defence for ${name}`}>
          <select className="xs" value={pending.level} aria-label="Level" onChange={(e) => setPending({ ...pending, level: e.target.value as DefenseLevel })}>
            {DEFENSE_LEVELS.map((l) => <option key={l.value} value={l.value} title={l.hint}>{l.label}</option>)}
          </select>
          <select className="xs" value={pending.option} aria-label="Damage type" onChange={(e) => setPending({ ...pending, option: e.target.value })}>
            {["everything", "weapons", "elemental & other"].map((g) => (
              <optgroup key={g} label={g}>
                {DEFENSE_TYPE_OPTIONS.filter((o) => o.group === g).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </optgroup>
            ))}
          </select>
          <select className="xs" value={pending.rounds} aria-label="How long" onChange={(e) => setPending({ ...pending, rounds: e.target.value })}>
            {DURATIONS.map((d) => <option key={d.label} value={d.rounds === null ? "" : String(d.rounds)}>{d.label}</option>)}
          </select>
          <input className="xs" value={pending.label} placeholder="from… (potion, spell)" aria-label="Where it came from" onChange={(e) => setPending({ ...pending, label: e.target.value, source: e.target.value.trim() ? "item" : "ruling" })} />
          <button className="ghost small" onClick={add}>add</button>
          <button className="ghost small" onClick={() => setPending(null)} aria-label="Cancel">×</button>
        </span>
      )}
    </>
  );
}
